/**
 * First-run tail entry. Figures come from what the user types, or from a
 * sim add-on performance row. Catalog Hawker / PC-12 presets are not copied.
 */
import { blankAircraft } from "./airports";
import { normalizeIcaoTypeDesignator } from "./company";
import type { SetupPerfHint } from "./aircraftDatabase";
import type { AircraftSpec } from "./types";

/** ICAO Doc 8643 designators are short. HS25B is accepted (addon-style, up to 8). */
const TYPE_RE = /^[A-Z][A-Z0-9]{1,7}$/;

export function icaoTypeDesignatorError(raw: string): string | null {
  const code = normalizeIcaoTypeDesignator(raw);
  if (!code) return "Type the ICAO type designator.";
  if (!TYPE_RE.test(code)) {
    return "Use 2–8 letters and digits, starting with a letter (H25B, PC12, GLEX, CL35, HS25B).";
  }
  return null;
}

export type SetupTailDraft = {
  id?: string;
  icaoType: string;
  registration: string;
  name: string;
  oewLb: number;
  mzfwLb: number;
  mlwLb: number;
  mtowLb: number;
  maxFuelLb: number;
  fuelBurnGph: number;
  /** Planning burn in pounds per hour. Gallons use the type density. */
  fuelBurnLbHr: number;
  fuelDensityLbPerGal: number;
  rangeNm: number;
  ferryRangeNm: number;
  cruiseKt: number;
  longRangeCruiseKt: number;
  averageCruiseKt: number;
  cruiseMach: number;
  climbRateFpm: number;
  descentRateFpm: number;
  engineHours: number;
  remarks: string;
  engines: string;
  cabin: string;
  year: string;
  role: string;
  maxPax: number;
  ceilingFt: number;
  takeoffDistanceFt: number;
  landingDistanceFt: number;
};

export function emptySetupTail(): SetupTailDraft {
  return {
    icaoType: "",
    registration: "",
    name: "",
    oewLb: 0,
    mzfwLb: 0,
    mlwLb: 0,
    mtowLb: 0,
    maxFuelLb: 0,
    fuelBurnGph: 0,
    fuelBurnLbHr: 0,
    fuelDensityLbPerGal: 0,
    rangeNm: 0,
    ferryRangeNm: 0,
    cruiseKt: 0,
    longRangeCruiseKt: 0,
    averageCruiseKt: 0,
    cruiseMach: 0,
    climbRateFpm: 0,
    descentRateFpm: 0,
    engineHours: 0,
    remarks: "",
    engines: "",
    cabin: "",
    year: "",
    role: "",
    maxPax: 0,
    ceilingFt: 0,
    takeoffDistanceFt: 0,
    landingDistanceFt: 0,
  };
}

function num(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/** Fill only figures the user has not typed yet. Typed numbers stay. Empty weight stays blank. */
export function applySimPerfToDraft(draft: SetupTailDraft, perf: SetupPerfHint): SetupTailDraft {
  const fill = (current: number, next: number | undefined) => (current > 0 ? current : num(next));
  const fuelBurnLbHr = fill(draft.fuelBurnLbHr, perf.fuelBurnLbHr);
  const fuelDensityLbPerGal = fill(draft.fuelDensityLbPerGal, perf.fuelDensityLbPerGal);
  const fuelBurnGph =
    draft.fuelBurnGph > 0
      ? draft.fuelBurnGph
      : fuelBurnLbHr > 0 && fuelDensityLbPerGal > 0
        ? fuelBurnLbHr / fuelDensityLbPerGal
        : num(perf.fuelBurnGph);
  return {
    ...draft,
    name: draft.name.trim() || perf.name?.trim() || draft.name,
    cruiseKt: fill(draft.cruiseKt, perf.cruiseKt),
    longRangeCruiseKt: fill(draft.longRangeCruiseKt, perf.longRangeCruiseKt),
    averageCruiseKt: fill(draft.averageCruiseKt, perf.averageCruiseKt),
    cruiseMach: fill(draft.cruiseMach, perf.cruiseMach),
    climbRateFpm: fill(draft.climbRateFpm, perf.climbRateFpm),
    descentRateFpm: fill(draft.descentRateFpm, perf.descentRateFpm),
    rangeNm: fill(draft.rangeNm, perf.rangeNm),
    ferryRangeNm: fill(draft.ferryRangeNm, perf.ferryRangeNm),
    fuelBurnLbHr,
    fuelDensityLbPerGal,
    fuelBurnGph,
    maxFuelLb: fill(draft.maxFuelLb, perf.maxFuelLb),
    mtowLb: fill(draft.mtowLb, perf.mtowLb),
    mlwLb: fill(draft.mlwLb, perf.mlwLb),
    mzfwLb: fill(draft.mzfwLb, perf.mzfwLb),
    takeoffDistanceFt: fill(draft.takeoffDistanceFt, perf.takeoffDistanceFt),
    landingDistanceFt: fill(draft.landingDistanceFt, perf.landingDistanceFt),
    ceilingFt: fill(draft.ceilingFt, perf.ceilingFt),
    maxPax: draft.maxPax > 0 ? draft.maxPax : num(perf.maxPax),
    engines: draft.engines.trim() || perf.engines?.trim() || draft.engines,
  };
}

export function aircraftFromSetupTail(draft: SetupTailDraft, baseIcao = ""): AircraftSpec {
  const registration = draft.registration.trim().toUpperCase().replace(/\s+/g, "");
  const icaoType = normalizeIcaoTypeDesignator(draft.icaoType);
  return blankAircraft({
    id: draft.id,
    name: draft.name.trim() || icaoType,
    registration,
    callsignPrefix: "",
    icaoType,
    cruiseKt: draft.cruiseKt,
    longRangeCruiseKt: draft.longRangeCruiseKt > 0 ? draft.longRangeCruiseKt : undefined,
    averageCruiseKt: draft.averageCruiseKt > 0 ? draft.averageCruiseKt : undefined,
    cruiseMach: draft.cruiseMach > 0 ? draft.cruiseMach : undefined,
    climbRateFpm: draft.climbRateFpm > 0 ? draft.climbRateFpm : undefined,
    descentRateFpm: draft.descentRateFpm > 0 ? draft.descentRateFpm : undefined,
    rangeNm: draft.rangeNm,
    ferryRangeNm: draft.ferryRangeNm > 0 ? draft.ferryRangeNm : draft.rangeNm,
    minRunwayFt: draft.takeoffDistanceFt,
    takeoffDistanceFt: draft.takeoffDistanceFt > 0 ? draft.takeoffDistanceFt : undefined,
    landingDistanceFt: draft.landingDistanceFt > 0 ? draft.landingDistanceFt : undefined,
    maxPax: draft.maxPax,
    fuelBurnGph: draft.fuelBurnGph,
    fuelBurnLbHr: draft.fuelBurnLbHr > 0 ? draft.fuelBurnLbHr : undefined,
    fuelDensityLbPerGal: draft.fuelDensityLbPerGal > 0 ? draft.fuelDensityLbPerGal : undefined,
    maxFuelLb: draft.maxFuelLb,
    oewLb: draft.oewLb,
    mzfwLb: draft.mzfwLb,
    mlwLb: draft.mlwLb,
    mtowLb: draft.mtowLb,
    engineHours: draft.engineHours,
    airframeHours: draft.engineHours,
    engines: draft.engines.trim(),
    ceilingFt: draft.ceilingFt,
    cabin: draft.cabin.trim(),
    year: draft.year.trim(),
    role: draft.role.trim(),
    remarks: draft.remarks.trim(),
    baseIcao: baseIcao.trim().toUpperCase(),
    lastAirportIcao: baseIcao.trim().toUpperCase(),
  });
}

export function setupTailError(draft: SetupTailDraft): string | null {
  const typeErr = icaoTypeDesignatorError(draft.icaoType);
  if (typeErr) return typeErr;
  if (!draft.registration.trim()) return "Type the tail or registration.";
  return null;
}
